import { io, Socket } from 'socket.io-client';
import { getSocketConfig, SOCKET_EVENTS, type SocketStatus, type HeartbeatData } from '../config/socket';
import { apiService } from '../../../shared/src/services/api/ApiService';
import { preprocessUserLatex } from '../../../shared/src/utils/latexPreprocessor';

// 简单的事件发射器实现，替代 Node.js 的 EventEmitter
class SimpleEventEmitter {
  private events: { [key: string]: Function[] } = {};

  on(event: string, callback: Function) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
  }

  off(event: string, callback?: Function) {
    if (!this.events[event]) return;
    
    if (callback) {
      this.events[event] = this.events[event].filter(cb => cb !== callback);
    } else {
      delete this.events[event];
    }
  }

  emit(event: string, ...args: any[]) {
    if (!this.events[event]) return;
    
    this.events[event].forEach(callback => {
      try {
        callback(...args);
      } catch (error) {
        // Event callback error - silently ignore
      }
    });
  }
}

export interface SocketMessage {
  sessionId?: string;
  message: string;
  model?: 'gemini' | 'deepseek';
  topicType?: 'image' | 'text';
}

export interface AIResponse {
  topicId?: string;
  message: string;
  messageId: string;
  tokenCount?: number;
  responseTime: number;
  model: string;
}

export interface TopicActivated {
  topicId: string;
  topicType: string;
  title: string;
  category: string;
  modelType: string;
}

export interface TopicStatus {
  topicId: string;
  status: string;
  reason?: string;
}

export interface SessionLimitReached {
  topicId: string;
  message: string;
  summary?: string;
  showContinueButton: boolean;
  showEndButton: boolean;
}

export interface RatingRequest {
  topicId: string;
  message: string;
  timeout: number;
  allowSkip: boolean;
}

export interface SocketError {
  code: string;
  message: string;
  details?: any;
}

/**
 * Socket.IO客户端服务
 * 完全按照参考设计的简单可靠连接机制
 */
class SocketService extends SimpleEventEmitter {
  private socket: Socket | null = null;
  private connecting = false; // 添加连接状态标记
  private url: string;
  private options: {
    token?: string;
  };

  constructor(url?: string, options: { token?: string } = {}) {
    super();
    
    // 处理不同环境的 URL 配置
    let socketUrl = url || import.meta.env.VITE_SOCKET_URL || 'http://localhost:5000';
    
    // 开发环境：如果是相对路径，使用当前域名
    if (socketUrl === '/' && import.meta.env.DEV) {
      socketUrl = window.location.origin;
    }
    // 生产环境：如果是相对路径，也使用当前域名
    else if (socketUrl === '/') {
      socketUrl = window.location.origin;
    }
    
    this.url = socketUrl;
    this.options = options;
    
    console.log('SocketService initialized:', {
      configUrl: import.meta.env.VITE_SOCKET_URL,
      finalUrl: this.url,
      isDev: import.meta.env.DEV,
      origin: window.location.origin
    });

    // 监听 ApiService 的 token 刷新事件
    this.setupTokenRefreshListener();
  }

  /**
   * 设置事件监听
   */
  private setupTokenRefreshListener(): void {
    // 监听 token 刷新事件
    apiService.on('token_refreshed', (data: { accessToken: string; refreshToken: string }) => {
      console.log('[SocketService] Token 已刷新，更新认证信息');
      
      // 更新 token
      this.options.token = data.accessToken;
      
      // 更新 Socket 的认证信息
      if (this.socket) {
        this.socket.auth = { token: data.accessToken };
        
        // 如果断开了，重新连接
        if (!this.socket.connected) {
          console.log('[SocketService] Socket 已断开，重新连接');
          this.socket.connect();
        }
      }
    });

    // 监听登出事件
    apiService.on('logout', () => {
      console.log('[SocketService] 收到登出通知，断开连接');
      this.disconnect();
    });

    console.log('[SocketService] 已注册事件监听');
  }

  /**
   * 连接到Socket.IO服务器
   * 完全按照参考设计：使用Socket.IO内置重连机制
   * 添加移动端特殊处理和调试信息
   */
  connect(): void {
    // 1. 防止重复连接
    if (this.socket?.connected || this.connecting) {
      console.log('Socket already connected or connecting, skipping...', {
        connected: this.socket?.connected,
        connecting: this.connecting,
        socketId: this.socket?.id
      });
      return;
    }

    // 2. 清理旧连接
    if (this.socket) {
      console.log('Cleaning up old socket connection...', {
        oldSocketId: this.socket.id,
        oldConnected: this.socket.connected
      });
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    // 3. 设置连接中状态
    this.connecting = true;
    console.log('Starting new socket connection...', {
      url: this.url,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    });

    const socketConfig = getSocketConfig();
    
    // 移动端检测和优化
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    console.log('Device detection:', { isMobile, userAgent: navigator.userAgent });
    
    // 移动端优化配置
    const mobileConfig = isMobile ? {
      transports: ['polling', 'websocket'], // 移动端优先使用polling
      upgrade: true,
      rememberUpgrade: false, // 不记住升级，每次重新协商
      timeout: 30000, // 增加超时时间
      forceNew: true, // 强制新连接
    } : {};

    this.socket = io(this.url, {
      autoConnect: false,
      ...socketConfig,
      ...mobileConfig,
      auth: this.options.token ? {
        token: this.options.token
      } : undefined
    });

    // 4. 监听连接结果，重置连接状态
    this.socket.on('connect', () => {
      this.connecting = false;
      console.log('Socket connected successfully', {
        socketId: this.socket?.id,
        transport: this.socket?.io?.engine?.transport?.name,
        url: this.url
      });
    });

    this.socket.on('connect_error', (error) => {
      this.connecting = false;
      console.error('Socket connection failed', {
        error: error.message,
        url: this.url,
        isMobile,
        timestamp: new Date().toISOString()
      });
    });

    // 添加更多调试事件
    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected', { reason, timestamp: new Date().toISOString() });
    });

    this.setupEventHandlers();
    this.setupMobileOptimizations();
    this.socket.connect();
  }

  /**
   * 移动端优化处理
   */
  private setupMobileOptimizations(): void {
    if (!this.socket) return;

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // 页面可见性变化处理
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          // 页面隐藏时，不主动断开连接，让服务器处理
        } else {
          // 页面重新可见时，检查连接状态
          if (this.socket && !this.socket.connected) {
            this.socket.connect();
          }
        }
      });

      // 移动端网络状态变化处理
      if ('onLine' in navigator) {
        window.addEventListener('online', () => {
          if (this.socket && !this.socket.connected) {
            this.socket.connect();
          }
        });
      }
    }
  }

  /**
   * 断开连接
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * 检查连接状态
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * 检查是否正在连接
   */
  isConnecting(): boolean {
    return this.connecting;
  }

  /**
   * 发送消息
   */
  sendMessage(data: SocketMessage): void {
    if (!this.socket || !this.socket.connected) {
      throw new Error('Socket not connected');
    }

    // 预处理用户消息中的LaTeX表达式
    const processedData = {
      ...data,
      message: preprocessUserLatex(data.message)
    };

    this.socket.emit(SOCKET_EVENTS.SEND_MESSAGE, processedData);
  }

  /**
   * 加入话题房间
   */
  joinTopic(topicId: string): void {
    if (!this.socket || !this.socket.connected) {
      throw new Error('Socket not connected');
    }

    this.socket.emit(SOCKET_EVENTS.JOIN_TOPIC, { topicId });
  }

  /**
   * 会话继续响应
   */
  sessionContinueResponse(topicId: string, confirmed: boolean): void {
    if (!this.socket || !this.socket.connected) {
      throw new Error('Socket not connected');
    }

    this.socket.emit(SOCKET_EVENTS.SESSION_CONTINUE_RESPONSE, { topicId, confirmed });
  }

  /**
   * 提交评价
   */
  submitRating(data: {
    topicId: string;
    rating?: number;
    feedback?: string;
    skipped?: boolean;
  }): void {
    if (!this.socket || !this.socket.connected) {
      throw new Error('Socket not connected');
    }

    this.socket.emit(SOCKET_EVENTS.SUBMIT_RATING, data);
  }

  // 移除自定义事件监听器管理，使用EventEmitter

  /**
   * 设置事件处理器
   * 完全按照参考设计：简单的心跳响应机制
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    // 连接成功
    this.socket.on(SOCKET_EVENTS.CONNECT, () => {
      this.emit(SOCKET_EVENTS.CONNECT);
    });

    // 连接断开 - 基于参考设计：依赖Socket.IO内置重连
    this.socket.on(SOCKET_EVENTS.DISCONNECT, (reason: string) => {
      this.emit(SOCKET_EVENTS.DISCONNECT, reason);
    });

    // 连接错误
    this.socket.on(SOCKET_EVENTS.CONNECT_ERROR, (error: Error) => {
      this.emit(SOCKET_EVENTS.CONNECT_ERROR, error);
    });

    // 心跳处理 - 完全按照参考设计的简单响应机制
    this.socket.on(SOCKET_EVENTS.HEARTBEAT, (data: HeartbeatData) => {
      if (this.socket?.connected) {
        this.socket.emit(SOCKET_EVENTS.HEARTBEAT_RESPONSE);
      }
    });

    // AI响应
    this.socket.on(SOCKET_EVENTS.AI_RESPONSE, (data: AIResponse) => {
      this.emit(SOCKET_EVENTS.AI_RESPONSE, data);
    });

    // 话题激活
    this.socket.on(SOCKET_EVENTS.TOPIC_ACTIVATED, (data: TopicActivated) => {
      this.emit(SOCKET_EVENTS.TOPIC_ACTIVATED, data);
    });

    // 话题恢复
    this.socket.on(SOCKET_EVENTS.TOPIC_RESUMED, (data: any) => {
      this.emit(SOCKET_EVENTS.TOPIC_RESUMED, data);
    });

    // 话题状态
    this.socket.on(SOCKET_EVENTS.TOPIC_STATUS, (data: TopicStatus) => {
      this.emit(SOCKET_EVENTS.TOPIC_STATUS, data);
    });

    // 话题加入成功
    this.socket.on(SOCKET_EVENTS.TOPIC_JOINED, (data: any) => {
      this.emit(SOCKET_EVENTS.TOPIC_JOINED, data);
    });

    // 会话限制
    this.socket.on(SOCKET_EVENTS.SESSION_LIMIT_REACHED, (data: SessionLimitReached) => {
      this.emit(SOCKET_EVENTS.SESSION_LIMIT_REACHED, data);
    });

    // 话题完成
    this.socket.on(SOCKET_EVENTS.TOPIC_COMPLETED, (data: any) => {
      this.emit(SOCKET_EVENTS.TOPIC_COMPLETED, data);
    });

    // 评价请求
    this.socket.on(SOCKET_EVENTS.RATING_REQUEST, (data: RatingRequest) => {
      this.emit(SOCKET_EVENTS.RATING_REQUEST, data);
    });

    // 评价提交成功
    this.socket.on(SOCKET_EVENTS.RATING_SUBMITTED, (data: any) => {
      this.emit(SOCKET_EVENTS.RATING_SUBMITTED, data);
    });

    // 消息限额达到
    this.socket.on(SOCKET_EVENTS.MESSAGE_LIMIT_REACHED, (data: any) => {
      this.emit(SOCKET_EVENTS.MESSAGE_LIMIT_REACHED, data);
    });

    // 认证错误处理
    this.socket.on('auth_error', (error: { message: string; code?: string }) => {
      console.warn('[SocketService] Socket 认证错误:', error);
      
      // 触发认证错误事件，让应用层处理
      this.emit('auth_error', error);
    });

    // 错误处理
    this.socket.on(SOCKET_EVENTS.ERROR, (error: SocketError) => {
      this.emit(SOCKET_EVENTS.ERROR, error);
    });
  }



  /**
   * 更新认证令牌
   */
  updateToken(token: string): void {
    // 验证token格式
    const isValidJWT = (token: string) => {
      if (!token || typeof token !== 'string' || token === 'undefined' || token === 'null' || token.trim() === '') {
        return false;
      }
      return token.split('.').length === 3;
    };
    
    if (!isValidJWT(token)) {
      console.warn('Invalid JWT token provided to updateToken', { 
        tokenPreview: token ? token.substring(0, 20) + '...' : 'none' 
      });
      return;
    }
    
    this.options.token = token;
    if (this.socket) {
      this.socket.auth = { token };
    }
  }

  /**
   * 获取连接信息
   */
  getConnectionInfo(): {
    connected: boolean;
    socketId?: string;
  } {
    return {
      connected: this.isConnected(),
      socketId: this.socket?.id,
    };
  }
}

// 导出单例实例
export const socketService = new SocketService();
export default SocketService;